import { acMensHubContract } from '@cortex/domain-ac-mens-hub-api';
import { buildEdgeAuthStartUrl } from '@cortex/core-types';
import { logError, logEvent } from '@cortex/faro';
import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { ContractRouterClient } from '@orpc/contract';
import { config } from '../config.js';

/**
 * aC Men's Hub web ↔ API の通信に使う oRPC 型付きクライアント。共有 contract
 * （@cortex/domain-ac-mens-hub-api）から型を導出するため、エンドポイント URL や手書きの
 * レスポンス型を介さず、API の入出力型がそのままクライアントに伝播する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub web から API を呼び出す oRPC client。contract-first で型安全に通信し、401 時は既存 REST fetch ラッパと同じ Edge Auth / login 遷移に揃える。
 * @graph-connects /rpc [calls] aC Men's Hub API oRPC endpoint
 */

/**
 * API base URL から oRPC endpoint URL を解決する。
 * @param apiUrl config の API base URL
 * @param origin relative URL 解決に使う browser origin
 * @returns oRPC RPC endpoint URL
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business absolute / relative の API base URL から /rpc endpoint を安定して組み立てる。
 * @graph-connects globalThis.location [references, via:origin] relative API URL 解決
 */
export function resolveRpcUrl(apiUrl: string, origin: string = globalThis.location.origin): string {
  const normalizedApiUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
  return new URL('rpc', new URL(normalizedApiUrl, origin)).toString();
}

/**
 * oRPC RPC link。Cookie 認証のため credentials を include し、401 は `request()` と同じく
 * Edge Auth start / login へ誘導する。ネットワーク到達不能と 401 は Faro に記録して
 * REST 経路と同じ可観測性を保つ。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub API /rpc への credential 付き transport。
 * @graph-connects /rpc [calls] aC Men's Hub API oRPC endpoint
 */
const link = new RPCLink({
  url: resolveRpcUrl(config.apiUrl),
  fetch: async (request, init) => {
    let response: Response;
    try {
      response = await globalThis.fetch(request, { ...init, credentials: 'include' });
    } catch (fetchError) {
      logError(fetchError instanceof Error ? fetchError : new Error(String(fetchError)), {
        event: 'orpc_request_network_failed',
        endpoint: request.url,
        method: request.method,
      });
      throw fetchError;
    }

    if (response.status === 401) {
      logEvent('orpc_request_unauthorized', {
        endpoint: request.url,
        method: request.method,
        edgeAuthStart: response.headers.get('X-Edge-Auth-Start') ? 'true' : 'false',
      });
      if (response.headers.get('X-Edge-Auth-Start')) {
        window.location.href = buildEdgeAuthStartUrl(window.location.href);
      } else if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return response;
  },
});

/**
 * aC Men's Hub API の型付き oRPC client。
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business `rpcClient.itemCatalog.list()` のように feature.procedure で呼び出す contract-first client。
 * @graph-connects /rpc [calls] aC Men's Hub API oRPC procedures
 */
export const rpcClient: ContractRouterClient<typeof acMensHubContract> = createORPCClient(link);
